import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning';
import { useAuth } from '../context/AuthContext';
import { useTimezone } from '../context/TimezoneContext';
import { getLocalDateString } from '../utils/dateUtils';
import { supportTaskService, storageService } from '../api';
import { useEditorImageUpload } from '../hooks/useEditorImageUpload';
import { getTaskTypeLabel, SupportTaskType, SupportTaskPriority, SupportTaskStatus, scopeStringToFlags, scopeFlagsToString } from '../api';
import { RELATION_TYPE_OPTIONS, getRelationTypeLabel } from '../api/services/supportTaskService';
import { X as XIcon, ArrowLeft, Search, Link2, Ban, ShieldAlert, Copy, CopyCheck } from 'lucide-react';
import { Button, Input, ThemedText, Avatar, RichTextEditor, ConfirmDialog } from '../components';
import { StatusPalette, SupportTaskStatusColors, SupportTaskPriorityColors, ProjectScopeColors } from '../constants/statusColors';
import { PageHeaderRow } from '../components/PageStyles';
import { FormLabel as Label, StyledSelect as Select } from '../components/FormStyles';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm || '0 1px 3px rgba(0,0,0,0.08)'};
`;

const SectionHeader = styled.div`
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionDivider = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const PriorityGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const PRIORITY_COLORS = SupportTaskPriorityColors;

const PriorityButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border: 2px solid ${props => PRIORITY_COLORS[props.$priority] || props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$selected ? PRIORITY_COLORS[props.$priority] || 'transparent' : 'transparent'};
  color: ${props => props.$selected
    ? (props.$priority === 1 ? '#000' : '#fff')
    : (PRIORITY_COLORS[props.$priority] || props.theme.colors.text)};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    opacity: 0.85;
    background-color: ${props => props.$selected
      ? PRIORITY_COLORS[props.$priority]
      : (PRIORITY_COLORS[props.$priority] || 'transparent') + '20'};
    ${props => props.$selected && `opacity: 0.85;`}
  }
`;

const DateFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const QuickDateChips = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  flex-wrap: wrap;
`;

const DateChip = styled.button`
  padding: 4px ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.$selected ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: 20px;
  background-color: ${props => props.$selected ? props.theme.colors.tint : 'transparent'};
  color: ${props => props.$selected ? '#fff' : props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    color: ${props => props.$selected ? '#fff' : props.theme.colors.tint};
  }
`;

const SentryInfoBanner = styled.div`
  background-color: ${props => props.theme.colors.tint}10;
  border: 1px solid ${props => props.theme.colors.tint}40;
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const SentryBannerTitle = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.default};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const SentryBannerDetail = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.text};
  word-break: break-all;
`;

const SentryLink = styled.a`
  color: ${props => props.theme.colors.tint};
  text-decoration: none;
  font-size: ${props => props.theme.typography.sizes.small};

  &:hover {
    text-decoration: underline;
  }
`;

const AIAnalysisBanner = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-left: 4px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const AIBannerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const AIBannerTitle = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
`;

const AIBannerToggle = styled.span`
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.small};
  user-select: none;
`;

const AIBannerBody = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const AIBannerSection = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
`;

const AIBannerSectionLabel = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const FileName = styled.div`
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.muted};
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: ${StatusPalette.red};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: transparent;
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.default};
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100%;
  text-align: center;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}08;
  }
`;

const UserChipsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const UserChip = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: 6px ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.$selected ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: 24px;
  background-color: ${props => props.$selected ? props.theme.colors.tint + '15' : 'transparent'};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}10;
  }
`;

const ReadOnlyInput = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.default};
  user-select: all;
`;

const PROJECT_SCOPE_COLORS = {
  portal: ProjectScopeColors.Internal,
  mobile: ProjectScopeColors.External,
};

const ProjectScopeButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border: 2px solid ${props => PROJECT_SCOPE_COLORS[props.$scope] || props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$selected ? PROJECT_SCOPE_COLORS[props.$scope] || 'transparent' : 'transparent'};
  color: ${props => props.$selected
    ? '#fff'
    : (PROJECT_SCOPE_COLORS[props.$scope] || props.theme.colors.text)};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  &:hover {
    opacity: 0.85;
    background-color: ${props => props.$selected
      ? PROJECT_SCOPE_COLORS[props.$scope]
      : (PROJECT_SCOPE_COLORS[props.$scope] || 'transparent') + '20'};
  }
`;

// Bitmask constants for project scope toggle logic
const SCOPE_PORTAL = 1;
const SCOPE_MOBILE = 2;

const HintText = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
`;

const RelationSearchWrapper = styled.div`
  position: relative;
`;

const RelationSearchRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: stretch;
`;

const RelationSearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  position: relative;

  svg {
    position: absolute;
    left: 10px;
    color: ${props => props.theme.colors.muted};
    pointer-events: none;
  }
`;

const RelationSearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  padding-left: 34px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  min-height: 40px;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.tint}20;
  }
`;

const RelationDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.md || '0 4px 12px rgba(0,0,0,0.12)'};
  max-height: 240px;
  overflow-y: auto;
  margin-top: 4px;
`;

const RelationDropdownItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${props => props.theme.colors.tint}10;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }
`;

const RelationDropdownItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
`;

const RelationDropdownItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const RelationMiniDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$color || props.theme.colors.muted};
`;

const RelationChipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.theme.spacing.sm};
`;

const RelationChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const RelationChipInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  min-width: 0;
  flex: 1;
`;

const RelationChipTaskNumber = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.tint};
  white-space: nowrap;
`;

const RelationChipTitle = styled.span`
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RelationTypeBadge = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  padding: 2px 8px;
  border-radius: 12px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const RelationRemoveButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.muted};
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: ${StatusPalette.red};
  }
`;

const RELATION_TYPE_CONFIG = {
  0: { icon: Link2, color: '#3b82f6', label: 'Related' },
  1: { icon: Ban, color: '#ef4444', label: 'Blocks' },
  2: { icon: ShieldAlert, color: '#f59e0b', label: 'Blocked By' },
  3: { icon: Copy, color: '#8b5cf6', label: 'Duplicate Of' },
  4: { icon: CopyCheck, color: '#8b5cf6', label: 'Duplicated By' },
};

const RelationTypeButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RelationTypeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid ${props => props.$selected
    ? props.$color
    : props.theme.colors.border};
  background: ${props => props.$selected
    ? props.$color + '14'
    : 'transparent'};
  color: ${props => props.$selected
    ? props.$color
    : props.theme.colors.textSecondary};

  &:hover {
    border-color: ${props => props.$color};
    color: ${props => props.$color};
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusSelectWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const DESCRIPTION_MAX = 5000;

const TYPE_OPTIONS = [
  { value: SupportTaskType.BUG, label: 'Bug' },
  { value: SupportTaskType.FEATURE, label: 'Feature' },
  { value: SupportTaskType.IMPROVEMENT, label: 'Improvement' },
  { value: SupportTaskType.QUESTION, label: 'Question' },
  { value: SupportTaskType.PROBLEM, label: 'Problem' },
];

const PRIORITY_OPTIONS = [
  { value: SupportTaskPriority.LOW, label: 'Low' },
  { value: SupportTaskPriority.MEDIUM, label: 'Medium' },
  { value: SupportTaskPriority.HIGH, label: 'High' },
  { value: SupportTaskPriority.CRITICAL, label: 'Critical' },
];

const STATUS_OPTIONS = [
  { value: SupportTaskStatus.OPEN, label: 'Open' },
  { value: SupportTaskStatus.IN_PROGRESS, label: 'In Progress' },
  { value: SupportTaskStatus.ON_HOLD, label: 'On Hold' },
  { value: SupportTaskStatus.RESOLVED, label: 'Resolved' },
  { value: SupportTaskStatus.CLOSED, label: 'Closed' },
  { value: SupportTaskStatus.BACKLOG, label: 'Backlog' },
];

const getQuickDate = (offset, timezoneId) => {
  const offsetDays = offset === 'tomorrow' ? 1 : offset === 'next_week' ? 7 : 0;
  if (offset === 'next_month') {
    const tz = timezoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  }
  return getLocalDateString(timezoneId, offsetDays);
};

const QUICK_DATES = [
  { label: 'Today', key: 'today' },
  { label: 'Tomorrow', key: 'tomorrow' },
  { label: 'Next Week', key: 'next_week' },
  { label: 'Next Month', key: 'next_month' },
];

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CreateTaskPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { isSuperAdmin, isAdmin } = useRole();
  const { user } = useAuth();
  const { effectiveTimezone } = useTimezone();
  const canEditTask = isAdmin() || !!user?.isInternalTaskOrganization;
  const isEditMode = Boolean(id);
  const fileInputRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingTask, setLoadingTask] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiExpanded, setAiExpanded] = useState(true);

  // Related tasks state
  const [relatedTasksToAdd, setRelatedTasksToAdd] = useState([]);
  const [existingRelations, setExistingRelations] = useState([]);
  const [relationSearchTerm, setRelationSearchTerm] = useState('');
  const [relationSearchResults, setRelationSearchResults] = useState([]);
  const [selectedRelationType, setSelectedRelationType] = useState(0);
  const [isSearchingRelations, setIsSearchingRelations] = useState(false);
  const [relationSearchDone, setRelationSearchDone] = useState(false);
  const relationSearchTimeoutRef = useRef(null);

  const hasSentryData = Boolean(searchParams.get('sentryIssueId') || searchParams.get('sentryTitle') || searchParams.get('sentryUrl'));

  // Determine default task type: OrgAdmin locked to Problem (4), SuperAdmin defaults to Bug (0)
  const defaultTaskType = (isSuperAdmin() || user?.isInternalTaskOrganization)
    ? String(SupportTaskType.BUG)
    : String(SupportTaskType.PROBLEM);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    taskType: defaultTaskType,
    priority: String(SupportTaskPriority.MEDIUM),
    status: String(SupportTaskStatus.OPEN),
    projectScope: 0,
    assignedToUserId: '',
    dueDate: '',
    sentryIssueId: '',
    sentryIssueUrl: '',
  });

  const [isDirty, setIsDirty] = useState(false);
  const dirtyEnabledRef = useRef(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, onConfirm: null });
  useUnsavedChangesWarning(isDirty);

  // Enable dirty tracking after initial mount + data population settles
  useEffect(() => {
    const timer = setTimeout(() => { dirtyEnabledRef.current = true; }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Track dirty state when form changes (skip initial population)
  useEffect(() => {
    if (!dirtyEnabledRef.current) return;
    setIsDirty(true);
  }, [formData]);

  const confirmLeave = (dest) => {
    if (!isDirty) { navigate(dest); return; }
    setConfirmState({
      isOpen: true,
      onConfirm: () => { setConfirmState(s => ({ ...s, isOpen: false })); navigate(dest); },
    });
  };

  // Pre-populate from URL query params (Sentry)
  useEffect(() => {
    if (isEditMode) return;

    const sentryIssueId = searchParams.get('sentryIssueId') || '';
    const sentryTitle = searchParams.get('sentryTitle') || '';
    const sentryUrl = searchParams.get('sentryUrl') || '';

    if (sentryIssueId || sentryTitle || sentryUrl) {
      setFormData(prev => ({
        ...prev,
        title: sentryTitle || prev.title,
        sentryIssueId: sentryIssueId,
        sentryIssueUrl: sentryUrl,
      }));
    }
  }, [searchParams, isEditMode]);

  // Pre-populate from location.state (AI analysis)
  useEffect(() => {
    if (isEditMode) return;

    const stateData = location.state?.aiAnalysis;
    if (stateData) {
      setAiAnalysis(stateData);
      setFormData(prev => ({
        ...prev,
        title: stateData.suggestedTitle || prev.title,
        description: stateData.suggestedDescription || prev.description,
        taskType: stateData.suggestedType !== undefined && stateData.suggestedType !== null
          ? String(stateData.suggestedType)
          : prev.taskType,
        priority: stateData.suggestedPriority !== undefined && stateData.suggestedPriority !== null
          ? String(stateData.suggestedPriority)
          : prev.priority,
      }));
    }
  }, [location.state, isEditMode]);

  // Load assignable users from the internal task organization
  useEffect(() => {
    if (!canEditTask) return;
    const loadUsers = async () => {
      try {
        const response = await supportTaskService.getAssignableUsers();
        const userList = response?.data || response || [];
        setUsers(Array.isArray(userList) ? userList : []);
      } catch {
        // Silently fail - assignment is optional
      }
    };
    loadUsers();
    // eslint-disable-next-line
  }, []);

  // Load existing task in edit mode
  useEffect(() => {
    if (!id) return;
    const loadTask = async () => {
      try {
        setLoadingTask(true);
        const response = await supportTaskService.getTaskById(id);
        const task = response.data || response;
        setFormData({
          title: task.title || '',
          description: task.description || '',
          taskType: task.taskType !== undefined ? task.taskType.toString() : defaultTaskType,
          priority: task.priority !== undefined ? task.priority.toString() : String(SupportTaskPriority.MEDIUM),
          status: task.status !== undefined ? task.status.toString() : String(SupportTaskStatus.OPEN),
          projectScope: task.projectScope !== undefined ? scopeStringToFlags(task.projectScope) : 0,
          assignedToUserId: task.assignedToUserId ? task.assignedToUserId.toString() : '',
          dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
          sentryIssueId: task.sentryIssueId || '',
          sentryIssueUrl: task.sentryIssueUrl || '',
        });
        // Load existing attachments if present
        if (task.attachments && Array.isArray(task.attachments)) {
          setExistingAttachments(task.attachments);
        } else if (task.attachments?.$values) {
          setExistingAttachments(task.attachments.$values);
        }
        // Load existing related tasks if present
        const relations = task.relatedTasks?.$values || task.relatedTasks;
        if (relations && Array.isArray(relations)) {
          setExistingRelations(relations);
        }
      // Reset dirty flag after loading edit data
        setTimeout(() => { setIsDirty(false); dirtyEnabledRef.current = true; }, 0);
      } catch {
        toast.error('Failed to load task');
        navigate('/tasks');
      } finally {
        setLoadingTask(false);
      }
    };
    loadTask();
  }, [id, navigate, defaultTaskType]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Track locally - actual upload happens on save via storageService
    setAttachments(prev => [...prev, {
      file,
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
    }]);
    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveNewAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = async (attachmentId) => {
    try {
      await supportTaskService.deleteAttachment(attachmentId);
      setExistingAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Attachment removed');
    } catch {
      toast.error('Failed to remove attachment');
    }
  };

  // Debounced relation search
  const handleRelationSearch = useCallback((term) => {
    setRelationSearchTerm(term);
    if (relationSearchTimeoutRef.current) {
      clearTimeout(relationSearchTimeoutRef.current);
    }
    if (!term.trim()) {
      setRelationSearchResults([]);
      setRelationSearchDone(false);
      return;
    }
    setRelationSearchDone(false);
    relationSearchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearchingRelations(true);
        const response = await supportTaskService.searchTasks(term, isEditMode ? id : undefined);
        const results = response?.data?.$values || response?.data || response || [];
        // Filter out tasks already in relatedTasksToAdd or existingRelations
        const existingIds = new Set([
          ...relatedTasksToAdd.map(r => r.taskId),
          ...existingRelations.map(r => r.relatedTaskId),
        ]);
        const filtered = (Array.isArray(results) ? results : []).filter(t => !existingIds.has(t.id));
        setRelationSearchResults(filtered);
        setRelationSearchDone(true);
      } catch {
        setRelationSearchResults([]);
        setRelationSearchDone(true);
      } finally {
        setIsSearchingRelations(false);
      }
    }, 300);
  }, [id, isEditMode, relatedTasksToAdd, existingRelations]);

  const handleAddRelation = async (task) => {
    setRelationSearchTerm('');
    setRelationSearchResults([]);
    setRelationSearchDone(false);
    const relationType = selectedRelationType;

    if (isEditMode) {
      try {
        const response = await supportTaskService.addRelation(id, {
          targetTaskId: task.id,
          relationType,
        });
        const relation = response?.data || response;
        setExistingRelations(prev => [...prev, {
          id: relation.id,
          relatedTaskId: task.id,
          taskNumber: task.taskNumber,
          title: task.title,
          relationType: RELATION_TYPE_OPTIONS[relationType]?.label || 'Related',
          status: task.status,
          priority: task.priority,
        }]);
        toast.success('Relation added');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to add relation');
      }
    } else {
      setRelatedTasksToAdd(prev => [...prev, {
        taskId: task.id,
        taskNumber: task.taskNumber,
        title: task.title,
        relationType,
        relationTypeLabel: RELATION_TYPE_OPTIONS[relationType]?.label || 'Related',
        status: task.status,
        priority: task.priority,
      }]);
    }
  };

  const handleRemoveRelation = async (relation) => {
    if (isEditMode && relation.id) {
      try {
        await supportTaskService.removeRelation(relation.id);
        setExistingRelations(prev => prev.filter(r => r.id !== relation.id));
        toast.success('Relation removed');
      } catch {
        toast.error('Failed to remove relation');
      }
    } else {
      setRelatedTasksToAdd(prev => prev.filter(r => r.taskId !== relation.taskId));
    }
  };

  const isOrgAdmin = isAdmin() && !isSuperAdmin() && !user?.isInternalTaskOrganization;
  const handleEditorImageUpload = useEditorImageUpload();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        title: formData.title,
        description: formData.description,
        taskType: formData.taskType,
        priority: formData.priority,
        projectScope: scopeFlagsToString(formData.projectScope),
        assignedToUserId: formData.assignedToUserId ? parseInt(formData.assignedToUserId) : null,
        dueDate: formData.dueDate || null,
        sentryIssueId: formData.sentryIssueId || null,
        sentryIssueUrl: formData.sentryIssueUrl || null,
      };

      let result;
      if (isEditMode) {
        result = await supportTaskService.updateTask(id, {
          ...payload,
          status: formData.status,
        });
      } else {
        result = await supportTaskService.createTask(payload);
      }

      const task = result.data || result;

      // Link related tasks (fire-and-forget in create mode)
      if (!isEditMode && relatedTasksToAdd.length > 0) {
        for (const r of relatedTasksToAdd) {
          supportTaskService.addRelation(task.id, {
            targetTaskId: r.taskId,
            relationType: r.relationType,
          }).catch(err => console.error('Failed to link related task:', err));
        }
      }

      // Upload and register new attachments
      if (attachments.length > 0) {
        for (const att of attachments) {
          if (att.file) {
            try {
              const { blobPath } = await storageService.uploadFileToBlob(att.file);
              await supportTaskService.addAttachment(task.id, {
                fileName: att.fileName,
                storagePath: blobPath,
                contentType: att.contentType,
                fileSize: att.fileSize,
              });
            } catch (uploadErr) {
              console.error('Failed to upload attachment:', att.fileName, uploadErr);
              toast.error(`Failed to upload ${att.fileName}`);
            }
          }
        }
      }

      const successMsg = isEditMode ? 'Task updated successfully' : (isOrgAdmin ? 'Problem reported successfully' : 'Task created successfully');
      toast.success(successMsg);
      setIsDirty(false);
      navigate('/tasks');
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} task:`, err);
      toast.error(err.response?.data?.message || err.message || 'Failed to save task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout currentPath="/tasks">
      <PageContainer theme={theme}>
        <PageHeaderRow theme={theme}>
          <div>
            <BackButton theme={theme} onClick={() => confirmLeave(isOrgAdmin ? '/dashboard' : '/tasks')}>
              <ArrowLeft size={14} /> {isOrgAdmin ? 'Back to Dashboard' : 'Back to Tasks'}
            </BackButton>
            <ThemedText type="title" style={{ marginTop: 8 }}>
              {isEditMode ? 'Edit Task' : (isOrgAdmin ? 'Report a Problem' : 'New Task')}
            </ThemedText>
          </div>
        </PageHeaderRow>

        <FormCard theme={theme}>
          <form onSubmit={handleSubmit}>

            {/* Sentry Info Banner */}
            {hasSentryData && (
              <SentryInfoBanner theme={theme}>
                <SentryBannerTitle theme={theme}>
                  Sentry Issue Linked
                </SentryBannerTitle>
                {formData.sentryIssueId && (
                  <SentryBannerDetail theme={theme}>
                    Issue ID: {formData.sentryIssueId}
                  </SentryBannerDetail>
                )}
                {formData.sentryIssueUrl && (
                  <SentryLink
                    href={formData.sentryIssueUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    theme={theme}
                  >
                    View in Sentry
                  </SentryLink>
                )}
              </SentryInfoBanner>
            )}

            {/* AI Analysis Banner */}
            {aiAnalysis && (
              <AIAnalysisBanner theme={theme}>
                <AIBannerHeader onClick={() => setAiExpanded(!aiExpanded)}>
                  <AIBannerTitle theme={theme}>AI Analysis</AIBannerTitle>
                  <AIBannerToggle theme={theme}>
                    {aiExpanded ? 'Collapse' : 'Expand'}
                  </AIBannerToggle>
                </AIBannerHeader>
                {aiExpanded && (
                  <AIBannerBody theme={theme}>
                    {aiAnalysis.analysis && (
                      <AIBannerSection theme={theme}>
                        <AIBannerSectionLabel theme={theme}>Analysis</AIBannerSectionLabel>
                        {aiAnalysis.analysis}
                      </AIBannerSection>
                    )}
                    {aiAnalysis.rootCause && (
                      <AIBannerSection theme={theme}>
                        <AIBannerSectionLabel theme={theme}>Root Cause</AIBannerSectionLabel>
                        {aiAnalysis.rootCause}
                      </AIBannerSection>
                    )}
                    {aiAnalysis.suggestedFix && (
                      <AIBannerSection theme={theme}>
                        <AIBannerSectionLabel theme={theme}>Suggested Fix</AIBannerSectionLabel>
                        {aiAnalysis.suggestedFix}
                      </AIBannerSection>
                    )}
                  </AIBannerBody>
                )}
              </AIAnalysisBanner>
            )}

            {/* Section: Details */}
            <SectionHeader theme={theme}>Details</SectionHeader>

            <FormGroup theme={theme}>
              <Label theme={theme}>Title *</Label>
              <Input
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter task title"
              />
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Description</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
                placeholder="Enter task description"
                variant="default"
                maxLength={DESCRIPTION_MAX}
                height={250}
                onImageUpload={handleEditorImageUpload}
              />
            </FormGroup>

            <FormRow theme={theme}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Type</Label>
                {isOrgAdmin ? (
                  <>
                    <ReadOnlyInput theme={theme}>
                      {getTaskTypeLabel(SupportTaskType.PROBLEM)}
                    </ReadOnlyInput>
                    <HintText theme={theme}>
                      Organization admins submit tasks as Problems.
                    </HintText>
                  </>
                ) : (
                  <Select
                    value={formData.taskType}
                    onChange={(e) => setFormData({ ...formData, taskType: e.target.value })}
                    theme={theme}
                  >
                    {TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                )}
              </FormGroup>

              {isEditMode && (
                <FormGroup theme={theme}>
                  <Label theme={theme}>Status</Label>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    theme={theme}
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              )}
            </FormRow>

            <FormGroup theme={theme}>
              <Label theme={theme}>Priority</Label>
              <PriorityGroup theme={theme}>
                {PRIORITY_OPTIONS.map(p => (
                  <PriorityButton
                    key={p.value}
                    type="button"
                    theme={theme}
                    $priority={p.value}
                    $selected={formData.priority === String(p.value)}
                    onClick={() => setFormData({ ...formData, priority: String(p.value) })}
                  >
                    {p.label}
                  </PriorityButton>
                ))}
              </PriorityGroup>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label theme={theme}>Project Scope</Label>
              <PriorityGroup theme={theme}>
                <ProjectScopeButton
                  type="button"
                  theme={theme}
                  $scope="portal"
                  $selected={Boolean(formData.projectScope & SCOPE_PORTAL)}
                  onClick={() => setFormData({ ...formData, projectScope: formData.projectScope ^ SCOPE_PORTAL })}
                >
                  Portal
                </ProjectScopeButton>
                <ProjectScopeButton
                  type="button"
                  theme={theme}
                  $scope="mobile"
                  $selected={Boolean(formData.projectScope & SCOPE_MOBILE)}
                  onClick={() => setFormData({ ...formData, projectScope: formData.projectScope ^ SCOPE_MOBILE })}
                >
                  Mobile App
                </ProjectScopeButton>
              </PriorityGroup>
              <HintText theme={theme}>
                Select which platforms are affected (multi-select).
              </HintText>
            </FormGroup>

            {/* Section: Assignment & Scheduling */}
            <SectionDivider theme={theme}>
              <SectionHeader theme={theme}>Assignment & Scheduling</SectionHeader>
            </SectionDivider>

            {canEditTask && (
              <FormGroup theme={theme}>
                <Label theme={theme}>Assigned To</Label>
                {users.length > 0 ? (
                  <UserChipsContainer theme={theme}>
                    {users.map(u => (
                      <UserChip
                        key={u.id}
                        type="button"
                        theme={theme}
                        $selected={formData.assignedToUserId === String(u.id)}
                        onClick={() => setFormData({
                          ...formData,
                          assignedToUserId: formData.assignedToUserId === String(u.id) ? '' : String(u.id),
                        })}
                      >
                        <Avatar src={u.profilePicture} name={`${u.firstName} ${u.lastName}`} size="sm" />
                        {u.firstName} {u.lastName}
                      </UserChip>
                    ))}
                  </UserChipsContainer>
                ) : (
                  <HintText theme={theme}>No assignable users found.</HintText>
                )}
              </FormGroup>
            )}

            <FormGroup theme={theme}>
              <Label theme={theme}>Due Date</Label>
              <DateFieldWrapper theme={theme}>
                <Input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                />
                <QuickDateChips theme={theme}>
                  {QUICK_DATES.map(qd => (
                    <DateChip
                      key={qd.key}
                      type="button"
                      theme={theme}
                      $selected={formData.dueDate === getQuickDate(qd.key, effectiveTimezone)}
                      onClick={() => setFormData({ ...formData, dueDate: getQuickDate(qd.key, effectiveTimezone) })}
                    >
                      {qd.label}
                    </DateChip>
                  ))}
                  {formData.dueDate && (
                    <DateChip
                      type="button"
                      theme={theme}
                      onClick={() => setFormData({ ...formData, dueDate: '' })}
                    >
                      Clear
                    </DateChip>
                  )}
                </QuickDateChips>
              </DateFieldWrapper>
            </FormGroup>

            {/* Section: Related Tasks */}
            <SectionDivider theme={theme}>
              <SectionHeader theme={theme}>Related Tasks</SectionHeader>
            </SectionDivider>

            <FormGroup theme={theme}>
              <Label theme={theme}>Related Tasks (optional)</Label>
              <RelationTypeButtonGroup>
                {RELATION_TYPE_OPTIONS.map(opt => {
                  const cfg = RELATION_TYPE_CONFIG[opt.value];
                  const Icon = cfg.icon;
                  return (
                    <RelationTypeButton
                      key={opt.value}
                      theme={theme}
                      type="button"
                      $selected={selectedRelationType === opt.value}
                      $color={cfg.color}
                      onClick={() => setSelectedRelationType(opt.value)}
                    >
                      <Icon size={13} />
                      {opt.label}
                    </RelationTypeButton>
                  );
                })}
              </RelationTypeButtonGroup>
              <RelationSearchWrapper>
                <RelationSearchRow theme={theme}>
                  <RelationSearchInputWrapper theme={theme}>
                    <Search size={16} />
                    <RelationSearchInput
                      theme={theme}
                      value={relationSearchTerm}
                      onChange={(e) => handleRelationSearch(e.target.value)}
                      placeholder="Search tasks by number or title..."
                    />
                  </RelationSearchInputWrapper>
                </RelationSearchRow>
                {(relationSearchResults.length > 0 || isSearchingRelations || relationSearchDone) && relationSearchTerm && (
                  <RelationDropdown theme={theme}>
                    {isSearchingRelations ? (
                      <RelationDropdownItem as="div" theme={theme} style={{ cursor: 'default' }}>
                        <RelationDropdownItemHeader theme={theme}>Searching...</RelationDropdownItemHeader>
                      </RelationDropdownItem>
                    ) : relationSearchResults.length === 0 ? (
                      <RelationDropdownItem as="div" theme={theme} style={{ cursor: 'default' }}>
                        <RelationDropdownItemHeader theme={theme}>No matching tasks found</RelationDropdownItemHeader>
                      </RelationDropdownItem>
                    ) : (
                      relationSearchResults.map(task => (
                        <RelationDropdownItem
                          key={task.id}
                          type="button"
                          theme={theme}
                          onClick={() => handleAddRelation(task)}
                        >
                          <RelationDropdownItemHeader theme={theme}>
                            <strong>{task.taskNumber}</strong>
                            <span>{task.title}</span>
                          </RelationDropdownItemHeader>
                          <RelationDropdownItemMeta theme={theme}>
                            <RelationMiniDot $color={SupportTaskStatusColors[task.status]} />
                            {task.status}
                            <span style={{ margin: '0 2px' }}>|</span>
                            <RelationMiniDot $color={SupportTaskPriorityColors[task.priority]} />
                            {task.priority}
                          </RelationDropdownItemMeta>
                        </RelationDropdownItem>
                      ))
                    )}
                  </RelationDropdown>
                )}
              </RelationSearchWrapper>

              {/* Existing relations (edit mode) */}
              {existingRelations.length > 0 && (
                <RelationChipsContainer theme={theme}>
                  {existingRelations.map(rel => (
                    <RelationChip key={rel.id} theme={theme}>
                      <RelationChipInfo theme={theme}>
                        <RelationChipTaskNumber theme={theme}>{rel.taskNumber}</RelationChipTaskNumber>
                        <RelationChipTitle theme={theme}>{rel.title}</RelationChipTitle>
                      </RelationChipInfo>
                      <RelationTypeBadge theme={theme}>
                        {typeof rel.relationType === 'string' ? getRelationTypeLabel(rel.relationType) : (RELATION_TYPE_OPTIONS[rel.relationType]?.label || 'Related')}
                      </RelationTypeBadge>
                      <RelationRemoveButton
                        type="button"
                        theme={theme}
                        onClick={() => handleRemoveRelation(rel)}
                        title="Remove relation"
                      >
                        <XIcon size={14} />
                      </RelationRemoveButton>
                    </RelationChip>
                  ))}
                </RelationChipsContainer>
              )}

              {/* Pending relations (create mode) */}
              {relatedTasksToAdd.length > 0 && (
                <RelationChipsContainer theme={theme}>
                  {relatedTasksToAdd.map(rel => (
                    <RelationChip key={rel.taskId} theme={theme}>
                      <RelationChipInfo theme={theme}>
                        <RelationChipTaskNumber theme={theme}>{rel.taskNumber}</RelationChipTaskNumber>
                        <RelationChipTitle theme={theme}>{rel.title}</RelationChipTitle>
                      </RelationChipInfo>
                      <RelationTypeBadge theme={theme}>
                        {rel.relationTypeLabel}
                      </RelationTypeBadge>
                      <RelationRemoveButton
                        type="button"
                        theme={theme}
                        onClick={() => handleRemoveRelation(rel)}
                        title="Remove relation"
                      >
                        <XIcon size={14} />
                      </RelationRemoveButton>
                    </RelationChip>
                  ))}
                </RelationChipsContainer>
              )}
            </FormGroup>

            {/* Section: Sentry Integration */}
            {(formData.sentryIssueId || formData.sentryIssueUrl || isEditMode) && (
              <>
                <SectionDivider theme={theme}>
                  <SectionHeader theme={theme}>Sentry Integration</SectionHeader>
                </SectionDivider>

                <FormRow theme={theme}>
                  <FormGroup theme={theme}>
                    <Label theme={theme}>Sentry Issue ID</Label>
                    {formData.sentryIssueId ? (
                      <ReadOnlyInput theme={theme}>{formData.sentryIssueId}</ReadOnlyInput>
                    ) : (
                      <Input
                        value={formData.sentryIssueId}
                        onChange={(e) => setFormData({ ...formData, sentryIssueId: e.target.value })}
                        placeholder="e.g. SENTRY-12345"
                      />
                    )}
                  </FormGroup>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>Sentry Issue URL</Label>
                    {formData.sentryIssueUrl ? (
                      <ReadOnlyInput theme={theme}>
                        <SentryLink
                          href={formData.sentryIssueUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          theme={theme}
                        >
                          {formData.sentryIssueUrl}
                        </SentryLink>
                      </ReadOnlyInput>
                    ) : (
                      <Input
                        value={formData.sentryIssueUrl}
                        onChange={(e) => setFormData({ ...formData, sentryIssueUrl: e.target.value })}
                        placeholder="https://sentry.io/..."
                      />
                    )}
                  </FormGroup>
                </FormRow>
              </>
            )}

            {/* Section: Attachments */}
            <SectionDivider theme={theme}>
              <SectionHeader theme={theme}>Attachments</SectionHeader>
            </SectionDivider>

            <FileList theme={theme}>
              {/* Existing attachments (edit mode) */}
              {existingAttachments.map(att => (
                <FileItem key={att.id} theme={theme}>
                  <FileInfo>
                    <FileName theme={theme}>{att.fileName}</FileName>
                    <FileSize theme={theme}>{formatFileSize(att.fileSize)}</FileSize>
                  </FileInfo>
                  <RemoveFileButton
                    type="button"
                    theme={theme}
                    onClick={() => handleRemoveExistingAttachment(att.id)}
                    title="Remove attachment"
                  >
                    <XIcon size={14} />
                  </RemoveFileButton>
                </FileItem>
              ))}

              {/* New attachments (pending upload) */}
              {attachments.map((att, index) => (
                <FileItem key={`new-${index}`} theme={theme}>
                  <FileInfo>
                    <FileName theme={theme}>{att.fileName}</FileName>
                    <FileSize theme={theme}>{formatFileSize(att.fileSize)}</FileSize>
                  </FileInfo>
                  <RemoveFileButton
                    type="button"
                    theme={theme}
                    onClick={() => handleRemoveNewAttachment(index)}
                    title="Remove attachment"
                  >
                    <XIcon size={14} />
                  </RemoveFileButton>
                </FileItem>
              ))}
            </FileList>

            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
            />

            <UploadButton
              type="button"
              theme={theme}
              onClick={() => fileInputRef.current?.click()}
              style={{ marginTop: (existingAttachments.length > 0 || attachments.length > 0) ? theme.spacing.sm : 0 }}
            >
              + Add Attachment
            </UploadButton>

            {/* Footer Actions */}
            <FooterActions theme={theme}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => confirmLeave('/tasks')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || loadingTask}>
                {submitting
                  ? (isEditMode ? 'Updating...' : 'Creating...')
                  : (isEditMode ? 'Update Task' : 'Create Task')}
              </Button>
            </FooterActions>
          </form>
        </FormCard>
      </PageContainer>

      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave?"
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState(s => ({ ...s, isOpen: false }))}
      />
    </Layout>
  );
};

export default CreateTaskPage;
export { CreateTaskPage };
